import React from 'react';
import { Link } from 'react-router-dom';

const FeatureCard = ({ title, description, icon, linkTo, darkMode }) => (
  <div className={`transition-theme duration-300 ease-in-out rounded-xl p-6 hover:transform hover:-translate-y-1 ${
    darkMode 
      ? 'bg-card-dark shadow-card-dark hover:shadow-lg border border-gray-700' 
      : 'bg-card-light shadow-card-light hover:shadow-xl'
  }`}>
    <div className={`text-4xl mb-4 ${darkMode ? 'text-secondary-dark' : 'text-primary-light'}`}>{icon}</div>
    <h3 className="text-xl font-bold mb-2">{title}</h3>
    <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
    <Link to={linkTo} className={`inline-flex items-center font-medium transition-colors ${
      darkMode ? 'text-secondary-dark hover:text-secondary-light' : 'text-primary-light hover:text-primary-dark'
    }`}>
      Try it now
      <svg className="ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
      </svg>
    </Link>
  </div>
);

const Home = ({ darkMode }) => {
  return (
    <div className="max-w-7xl mx-auto">
      <div className={`text-center mb-12 ${darkMode ? 'bg-gray-800 rounded-2xl p-8 shadow-lg' : 'bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow'}`}>
        <h1 className={`text-4xl md:text-5xl font-extrabold mb-4 ${darkMode ? 'bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-teal-400' : 'text-gray-900'}`}>
          Welcome to Blox Fruits Calculator
        </h1>
        <p className={`text-xl md:text-2xl max-w-3xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Optimize your builds, calculate damage outputs, and plan fruit acquisitions efficiently
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        <FeatureCard 
          title="Character Build Optimization" 
          description="Input your character stats and get real-time suggestions for ideal stat allocation."
          icon="📊"
          linkTo="/calculator"
          darkMode={darkMode}
        />
        <FeatureCard 
          title="Damage Calculation" 
          description="Compute damage based on equipped fruit, weapons, and stats."
          icon="⚔️"
          linkTo="/calculator"
          darkMode={darkMode}
        />
        <FeatureCard 
          title="Fruit Acquisition Planner" 
          description="Track your fruit inventory and get recommendations based on your playstyle."
          icon="🍎"
          linkTo="/fruit-planner"
          darkMode={darkMode}
        />
        <FeatureCard 
          title="Comparison Tools" 
          description="Side-by-side comparison of different fruits, weapons, and abilities."
          icon="🔍"
          linkTo="/comparison"
          darkMode={darkMode}
        />
        <FeatureCard 
          title="Save & Load Builds" 
          description="Save your favorite builds and load them whenever you need."
          icon="💾"
          linkTo="/calculator"
          darkMode={darkMode}
        />
        <FeatureCard 
          title="Visual Stats" 
          description="Visual representation of stats and skill damage with interactive charts."
          icon="📈"
          linkTo="/comparison"
          darkMode={darkMode}
        />
      </div>
      
      <div className={`text-center mb-8 py-10 px-6 rounded-xl ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 shadow-lg' 
          : 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md'
      }`}>
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white">Ready to optimize your gameplay?</h2>
        <Link 
          to="/calculator" 
          className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-medium transition-colors ${
            darkMode 
              ? 'bg-secondary-dark text-white hover:bg-secondary-light' 
              : 'bg-white text-blue-600 hover:bg-blue-50'
          }`}
        >
          Get Started Now
          <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default Home;
